/**
 * Web implementation of `react-native-image-picker` — LOCAL DEV TOOLING ONLY.
 *
 * Only consumer: src/features/capture-face/ui/FaceCaptureField.tsx, which grabs a face
 * photo for enrolment and reads `res.assets[0].base64`. It expects BARE base64 (no
 * `data:` prefix) — that is what fetchPersonnel returns and what the Avatar/data-URI
 * helper re-wraps — so we strip the prefix that canvas.toDataURL produces.
 *
 * Both entry points open a real file dialog, so a browser tester can enrol a face from
 * a file on the PC or phone:
 *   - launchImageLibrary → plain picker ("Из галереи")
 *   - launchCamera       → same picker with `capture`, which a mobile browser opens
 *                          straight into the camera; desktop browsers ignore the hint
 *                          and show the file dialog, the closest honest equivalent to
 *                          "Сделать фото" on a machine without one.
 *
 * The picked image is downscaled to fit maxWidth/maxHeight and re-encoded as JPEG at
 * `quality`, mirroring what the native picker does with the same PICK_OPTS — so the
 * sync payload to the terminal stays the size it would be coming from a phone.
 */

/** Native returns {didCancel} / {errorCode, errorMessage} / {assets:[…]} — match it. */
const CANCELLED = {didCancel: true};

function pickFile({capture}) {
  return new Promise(resolve => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    if (capture) {
      input.capture = 'user';
    }
    // Must be in the DOM for the dialog to open reliably across browsers, but kept off
    // screen so it never shifts the RNW layout while attached.
    input.style.position = 'fixed';
    input.style.left = '-9999px';
    document.body.appendChild(input);

    let settled = false;
    const finish = value => {
      if (settled) {
        return;
      }
      settled = true;
      input.remove();
      resolve(value);
    };

    input.addEventListener('change', () => finish(input.files?.[0] ?? null));
    // Fired when the user dismisses the dialog. Supported in current Chrome/Firefox;
    // where it is not, the promise stays pending until a later pick — which the caller
    // already guards against with its `busy` flag.
    input.addEventListener('cancel', () => finish(null));

    input.click();
  });
}

/** Draw onto a canvas at the capped size and read back JPEG bytes. */
function downscaleToJpegBase64(file, {maxWidth, maxHeight, quality}) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(url);
      const {width: w, height: h} = img;
      // Only ever shrink: scaling a small photo up would inflate the payload without
      // adding detail the terminal could use.
      const scale = Math.min(1, maxWidth / w, maxHeight / h);
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(w * scale));
      canvas.height = Math.max(1, Math.round(h * scale));

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('canvas 2d context unavailable'));
        return;
      }
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      const dataUrl = canvas.toDataURL('image/jpeg', quality);
      resolve({
        base64: dataUrl.slice(dataUrl.indexOf(',') + 1),
        width: canvas.width,
        height: canvas.height,
      });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('image decode failed'));
    };

    img.src = url;
  });
}

async function pick(options, {capture}) {
  const {maxWidth = 1280, maxHeight = 1280, quality = 0.8} = options ?? {};

  const file = await pickFile({capture});
  if (!file) {
    return CANCELLED;
  }

  try {
    const {base64, width, height} = await downscaleToJpegBase64(file, {
      maxWidth,
      maxHeight,
      quality,
    });
    return {
      assets: [
        {
          base64,
          uri: `data:image/jpeg;base64,${base64}`,
          type: 'image/jpeg',
          fileName: file.name,
          fileSize: Math.round((base64.length * 3) / 4),
          width,
          height,
        },
      ],
    };
  } catch (e) {
    // FaceCaptureField maps every code except 'permission' to a generic error message.
    return {errorCode: 'others', errorMessage: String(e?.message ?? e)};
  }
}

export async function launchCamera(options) {
  return pick(options, {capture: true});
}

export async function launchImageLibrary(options) {
  return pick(options, {capture: false});
}

export default {launchCamera, launchImageLibrary};
